import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCreateProject, useUpdateProject } from "@/hooks/queries";

const field = "w-full rounded-xl border border-white/10 bg-ink-900 px-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/25 focus:outline-none";

export function ProjectFormDialog({ open, onOpenChange, project, onSaved }) {
  const [name, setName] = useState(project?.name || "");
  const [description, setDescription] = useState(project?.description || "");
  const [instructions, setInstructions] = useState(project?.instructions || "");
  const create = useCreateProject();
  const update = useUpdateProject();
  const busy = create.isPending || update.isPending;
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name: name.trim(), description, instructions };
      const saved = project ? await update.mutateAsync({ id: project.id, ...body }) : await create.mutateAsync(body);
      toast.success(project ? "Project updated" : "Project created");
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-lg" data-testid="project-form-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="text-white">{project ? "Edit project" : "New project"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Projects keep related chats and files together, so ChatSleek can use their context.</DialogDescription>
          </DialogHeader>
          <label className="mt-5 block text-xs text-zinc-400" htmlFor="project-name">Name</label>
          <input id="project-name" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Product launch" className={`${field} mt-1.5 h-11`} data-testid="project-name-input" />
          <label className="mt-4 block text-xs text-zinc-400" htmlFor="project-description">Description <span className="text-zinc-600">(optional)</span></label>
          <textarea id="project-description" value={description} maxLength={2000} rows={2} onChange={(e) => setDescription(e.target.value)} placeholder="What is this project about?" className={`${field} mt-1.5 resize-none py-2.5`} data-testid="project-description-input" />
          <label className="mt-4 block text-xs text-zinc-400" htmlFor="project-instructions">Instructions for ChatSleek <span className="text-zinc-600">(optional)</span></label>
          <textarea id="project-instructions" value={instructions} maxLength={8000} rows={4} onChange={(e) => setInstructions(e.target.value)} placeholder="How should ChatSleek answer in this project? Tone, audience, constraints…" className={`${field} mt-1.5 resize-none py-2.5`} data-testid="project-instructions-input" />
          <DialogFooter className="mt-6 gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:bg-white/[0.05]" data-testid="project-form-cancel">Cancel</button>
            <button type="submit" disabled={!name.trim() || busy} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 disabled:opacity-50" data-testid="project-form-save">
              {project ? "Save" : "Create project"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
